import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  organizationMembershipColumns,
  tableColumnsV2,
} from '../../../tableColumns/membershipColumns';
import { history, toTitleCase } from '../../../utils';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import Card from '@mui/material/Card';
import { MovaticFieldGroupSingleSelectFilter } from '../../../components/Redesing/movatic-field-group';
import { ISortConfig } from '../../../ts/interface/components/redesing/table.interface';
import { SearchChip } from '../../../ts/interface/components/table.interface';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useMembershipImages from '../../../hooks/membership/query/useMembershipImages';
import { MenuItem } from '@mui/material';
import { IMembership, IFilters } from '../../../ts/interface/membership.interface';
import useOrganization from '../../../hooks/use-organization';
import OrganizationSystemMultiSelect from '../../../components/system/organization-system-multi-select';

interface IMembershipListProps {
  memberships: IMembership[];
  isLoading: boolean;
  isLoadingCache: boolean;
  hasMore: boolean;
  pageNumber: number;
  showSystemColumn?: boolean;
  filters: IFilters;
  setFilters: React.Dispatch<React.SetStateAction<IFilters>>;
  isFilterComplete: boolean;
  isRestoring: boolean;
  totalItems?: number;
  onPageChange: (page: number) => void;
  onSizePerPageChange: (sizePerPage: number) => void;
  onSearchChange?: (search: string) => void;
  onSortChange?: (sort: ISortConfig) => void;
  onFilterApply?: () => void;
}

const filterOptions = [
  // Empty value clears the status filter so all memberships are listed.
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'future', label: 'Future' },
];

const MembershipList: React.FC<IMembershipListProps> = (props) => {
  const {
    memberships,
    isLoading,
    isLoadingCache,
    hasMore,
    pageNumber,
    filters,
    setFilters,
    isFilterComplete,
    onSizePerPageChange,
    onPageChange,
    isRestoring,
    showSystemColumn = false,
    totalItems,
    onSearchChange,
    onSortChange,
    onFilterApply,
  } = props;

  const organizationInfo = useOrganization();

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Pending single-select status value (null = no selection). Seeded from the
  // applied, URL-persisted status so a refresh shows the right dropdown state.
  const [value, setValue] = React.useState<{ value: string; label: string } | null>(
    filters.status ? { value: filters.status, label: toTitleCase(filters.status) } : null
  );
  const [exportData, setExportData] = useState<boolean>(false);

  const { data: membershipImages } = useMembershipImages(
    {},
    { organization_id: organizationInfo.id }
  );

  const sorting: ISortConfig = useMemo(() => {
    return {
      key: filters.sortConfig.key || 'name',
      direction: filters.sortConfig.direction || 'asc',
    };
  }, [filters.sortConfig.key, filters.sortConfig.direction]);

  // Rebuild the full chip list from the *applied* filters (the same
  // URL-persisted fields that drive the query), so the chips always match the
  // fetched data — including after a refresh and including when a filter was
  // cleared (no chip). Deriving from the applied fields rather than the pending
  // `value` keeps chips and query in lock-step.
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    if (filters.status) {
      next.push({
        label: 'Status',
        field: 'status',
        value: filters.status,
        displayValue: toTitleCase(filters.status),
      });
    }

    if (showSystemColumn) {
      filters.system
        ?.split(',')
        .filter(Boolean)
        .forEach((systemValue: string) => {
          const option = filters.selectedSystem?.find(
            (item) => item.value === systemValue
          );
          next.push({
            label: 'System',
            field: 'system',
            value: systemValue,
            displayValue: option?.label ?? systemValue,
          });
        });
    }

    setChips(next);
  }, [
    isRestoring,
    filters.status,
    filters.system,
    filters.selectedSystem,
    showSystemColumn,
  ]);

  const columns = useCallback(() => {
    return showSystemColumn
      ? organizationMembershipColumns(filters.status, membershipImages)
      : tableColumnsV2(filters.status, membershipImages);
  }, [filters, membershipImages, showSystemColumn]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      if (deletedGroup.field === 'status') {
        setValue(null);
        setFilters((prevState) => ({ ...prevState, status: '' }));
        onFilterApply?.();
        return;
      }

      if (deletedGroup.field === 'system') {
        setFilters((prevState) => {
          const remainingSystems =
            prevState.selectedSystem?.filter(
              (item) => item.value !== deletedGroup.value
            ) ?? [];
          return {
            ...prevState,
            selectedSystem: remainingSystems,
            system: remainingSystems.map((item) => item.value).join(','),
          };
        });
        onFilterApply?.();
        return;
      }

      onFilterApply?.();
    },
    [setValue, setFilters, onFilterApply]
  );

  return (
    <Card>
      <PaginationTable
        showFilter
        isLoading={isLoading || !isFilterComplete}
        isLoadingCache={isLoadingCache || !isFilterComplete}
        padding={{
          pr: 1,
          pt: 1,
        }}
        sortBy={sorting.key}
        onSortChange={(sort) => {
          setFilters((prevState) => ({
            ...prevState,
            sortConfig: sort as ISortConfig,
          }));
          onSortChange?.(sort as ISortConfig);
        }}
        items={memberships}
        noDataText={`There are no memberships at this time`}
        onRowCLick={(id: string) => history.push(`/memberships/${id}/general`)}
        chips={chips}
        handleChipDelete={handleChipDelete}
        onApplyFilter={() => {
          // Commit the pending selections into the URL-persisted filters that
          // drive the query; the chips effect rebuilds from these applied values.
          setFilters((prevState) => ({
            ...prevState,
            status: value?.value ?? '',
            system: prevState.selectedSystem?.map((item) => item.value).join(',') ?? '',
          }));
          onFilterApply?.();
        }}
        spaceFilters={
          <>
            <MovaticFieldGroupSingleSelectFilter
              label={'Status'}
              value={value?.value ?? ''}
              onChange={(newValue) => {
                const selectedOption = filterOptions.find(
                  (option) => option.value === newValue.target.value
                );
                setValue(
                  selectedOption
                    ? { value: selectedOption.value, label: selectedOption.label }
                    : null
                );
              }}
              options={filterOptions.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            />

            {showSystemColumn && (
              <>
                <OrganizationSystemMultiSelect
                  organization_id={organizationInfo.id}
                  selectedSystem={filters.selectedSystem}
                  onChange={(systems) => {
                    setFilters((prevState) => ({
                      ...prevState,
                      selectedSystem: systems.map((system) => ({
                        label: system?.name,
                        value: system?.gid,
                        original: system,
                      })),
                    }));
                  }}
                />
              </>
            )}
          </>
        }
        buttons={[
          {
            label: 'Export',
            onClick: () => setExportData(true),
            startIcon: <FileDownloadIcon />,
          },
          {
            label: 'Add',
            onClick: () => history.push('/memberships/add'),
            startIcon: <AddIcon />,
            variant: 'contained',
          },
        ]}
        localSearch={false}
        noDataSlice
        columns={columns()}
        exportData={exportData}
        csvFileName={'Memberships.csv'}
        onExport={() => setExportData(false)}
        rowsPerPage={filters.sizePerPage}
        totalItems={totalItems}
        hasMore={hasMore}
        page={pageNumber}
        onPageChange={(page, pageN: number) => onPageChange(pageN)}
        onRowsPerPageChange={(rowsPerPage) => {
          onSizePerPageChange(Number(rowsPerPage.target.value));
        }}
        searchText={filters.search}
        searchPlaceholder={`Search memberships`}
        handleSearch={(search: React.ChangeEvent<HTMLInputElement>) => {
          setFilters((prevState) => ({
            ...prevState,
            search: search.target.value,
          }));
          onSearchChange?.(search.target.value);
        }}
      />
    </Card>
  );
};

export default MembershipList;
